// ═══════════════════════════════════════════════════════════════════════
// Embed providers — URL parsing for video_embed + social_embed sections.
//
// Pure functions. Used by:
//   • apps/portal/src/app/content/sectionEditors.tsx — auto-detect
//     provider from the pasted URL so authors don't pick from a dropdown.
//   • apps/storefront/src/components/embeds/* — build embed URLs at
//     render time (iframe src, script src, blockquote attributes).
//
// Adding a Tier 2 option or a new provider variant is editor-side and
// storefront-side; the schema's open-shape `options` blob keeps this
// file the only place that needs to learn the new shape.
// ═══════════════════════════════════════════════════════════════════════

export type VideoProvider = 'youtube' | 'vimeo' | 'wistia'
export type SocialProvider = 'instagram' | 'tiktok' | 'x'
export type EmbedProvider = VideoProvider | SocialProvider

export type DetectedVideo =
  | { provider: 'youtube'; id: string; isShorts: boolean }
  | { provider: 'vimeo'; id: string }
  | { provider: 'wistia'; id: string }

export type DetectedSocial =
  | { provider: 'instagram'; id: string; isReel: boolean }
  | { provider: 'tiktok'; id: string; user: string }
  | { provider: 'x'; id: string; user: string }

// ---------------------------------------------------------------------
// URL → provider detectors
// ---------------------------------------------------------------------

export function detectVideoProvider(rawUrl: string): DetectedVideo | null {
  const url = parseUrl(rawUrl)
  if (!url) return null
  const host = url.hostname.toLowerCase().replace(/^www\./, '')

  // YouTube: youtube.com/watch?v=ID, youtu.be/ID, youtube.com/embed/ID, /shorts/ID
  if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
    const v = url.searchParams.get('v')
    if (v) return { provider: 'youtube', id: v, isShorts: false }
    const m = url.pathname.match(/^\/(embed|shorts)\/([A-Za-z0-9_-]{6,})/)
    if (m) return { provider: 'youtube', id: m[2], isShorts: m[1] === 'shorts' }
  }
  if (host === 'youtu.be') {
    const id = url.pathname.replace(/^\//, '').split('/')[0]
    if (id) return { provider: 'youtube', id, isShorts: false }
  }

  // Vimeo: vimeo.com/ID  (player.vimeo.com/video/ID also works)
  if (host === 'vimeo.com') {
    const id = url.pathname.split('/').filter(Boolean)[0]
    if (id && /^\d+$/.test(id)) return { provider: 'vimeo', id }
  }
  if (host === 'player.vimeo.com') {
    const m = url.pathname.match(/^\/video\/(\d+)/)
    if (m) return { provider: 'vimeo', id: m[1] }
  }

  // Wistia: SUBDOMAIN.wistia.com/medias/HASH, fast.wistia.com/embed/iframe/HASH
  if (host.endsWith('.wistia.com') || host === 'wistia.com') {
    const m = url.pathname.match(/\/medias\/([A-Za-z0-9]+)/)
    if (m) return { provider: 'wistia', id: m[1] }
    const m2 = url.pathname.match(/\/embed\/iframe\/([A-Za-z0-9]+)/)
    if (m2) return { provider: 'wistia', id: m2[1] }
  }

  return null
}

export function detectSocialProvider(rawUrl: string): DetectedSocial | null {
  const url = parseUrl(rawUrl)
  if (!url) return null
  const host = url.hostname.toLowerCase().replace(/^www\./, '')

  // Instagram: instagram.com/p/ID, instagram.com/reel/ID
  if (host === 'instagram.com') {
    const m = url.pathname.match(/^\/(p|reel|tv)\/([A-Za-z0-9_-]+)/)
    if (m) return { provider: 'instagram', id: m[2], isReel: m[1] === 'reel' }
  }

  // TikTok: tiktok.com/@user/video/ID
  if (host === 'tiktok.com' || host === 'm.tiktok.com') {
    const m = url.pathname.match(/^\/@([A-Za-z0-9._]+)\/video\/(\d+)/)
    if (m) return { provider: 'tiktok', user: m[1], id: m[2] }
  }

  // X / Twitter: twitter.com/user/status/ID, x.com/user/status/ID
  if (host === 'twitter.com' || host === 'x.com') {
    const m = url.pathname.match(/^\/([A-Za-z0-9_]+)\/status\/(\d+)/)
    if (m) return { provider: 'x', user: m[1], id: m[2] }
  }

  return null
}

// ---------------------------------------------------------------------
// Storefront-render helpers — iframe src builders per provider
// ---------------------------------------------------------------------

export type EmbedOptions = Record<string, string | number | boolean>

export function buildVideoEmbedSrc(
  detected: DetectedVideo,
  opts: EmbedOptions = {},
): string {
  const o = (k: string, fallback?: string | number | boolean) =>
    opts[k] ?? fallback
  const bool = (k: string, fallback = false): boolean =>
    typeof opts[k] === 'boolean' ? (opts[k] as boolean) : fallback

  if (detected.provider === 'youtube') {
    const host = bool('privacy_mode', true)
      ? 'www.youtube-nocookie.com'
      : 'www.youtube.com'
    const params = new URLSearchParams()
    if (bool('autoplay')) {
      params.set('autoplay', '1')
      params.set('mute', '1')
    }
    if (bool('loop')) {
      params.set('loop', '1')
      params.set('playlist', detected.id)
    }
    if (bool('hide_controls')) params.set('controls', '0')
    if (bool('limit_related', true)) params.set('rel', '0')
    const start = Number(o('start_time', 0))
    if (start > 0) params.set('start', String(start))
    const qs = params.toString()
    return `https://${host}/embed/${detected.id}${qs ? `?${qs}` : ''}`
  }

  if (detected.provider === 'vimeo') {
    const params = new URLSearchParams()
    if (bool('privacy_mode', true)) params.set('dnt', '1')
    if (bool('autoplay')) {
      params.set('autoplay', '1')
      params.set('muted', '1')
    }
    if (bool('loop')) params.set('loop', '1')
    if (bool('hide_controls')) params.set('controls', '0')
    if (bool('hide_meta')) {
      params.set('title', '0')
      params.set('byline', '0')
      params.set('portrait', '0')
    }
    const accent = String(o('accent_color', '') || '')
    if (/^[0-9a-f]{6}$/i.test(accent)) params.set('color', accent)
    const start = Number(o('start_time', 0))
    if (start > 0) params.set('#t', `${start}s`)
    const qs = params.toString()
    return `https://player.vimeo.com/video/${detected.id}${qs ? `?${qs}` : ''}`
  }

  // wistia — iframe via fast.wistia.com/embed/iframe/HASH
  const params = new URLSearchParams()
  if (bool('autoplay')) params.set('autoPlay', 'true')
  if (bool('loop')) params.set('endVideoBehavior', 'loop')
  if (bool('hide_controls')) params.set('playerControls', 'false')
  const qs = params.toString()
  return `https://fast.wistia.com/embed/iframe/${detected.id}${qs ? `?${qs}` : ''}`
}

// ---------------------------------------------------------------------
// Aspect ratio per detection (for storefront wrapper)
// ---------------------------------------------------------------------

export function aspectRatioFor(d: DetectedVideo | DetectedSocial): string {
  if (d.provider === 'youtube' && d.isShorts) return '9 / 16'
  if (d.provider === 'tiktok') return '9 / 16'
  if (d.provider === 'instagram') return d.isReel ? '9 / 16' : '4 / 5'
  return '16 / 9'
}

// ---------------------------------------------------------------------
// Provider script URLs (social embed loader)
// ---------------------------------------------------------------------

export const SOCIAL_SCRIPT: Record<SocialProvider, string> = {
  instagram: 'https://www.instagram.com/embed.js',
  tiktok: 'https://www.tiktok.com/embed.js',
  x: 'https://platform.twitter.com/widgets.js',
}

// ---------------------------------------------------------------------
// Internal
// ---------------------------------------------------------------------

function parseUrl(raw: string): URL | null {
  try {
    return new URL(raw.trim())
  } catch {
    return null
  }
}
